import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message } from 'antd';
import { getNotificationInstance } from './utils/messageInstance';
import { notification } from 'antd'; // 添加静态导入作为后备

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  code?: string;
  errorMessage?: string;
  msg?: string;
  showType?: ErrorShowType;
}

enum ApiStatus {
  success = '00000', // 成功
  error = 400, // 错误
  unauthorized = 'A0002', // 未授权
  forbidden = 403, // 禁止访问
  notFound = 404, // 未找到
  methodNotAllowed = 405, // 方法不允许
  requestTimeout = 408, // 请求超时
  internalServerError = 500, // 服务器错误
  notImplemented = 501, // 未实现
  badGateway = 502, // 网关错误
  serviceUnavailable = 503, // 服务不可用
  gatewayTimeout = 504, // 网关超时
  httpVersionNotSupported = 505, // HTTP版本不支持
}
const clearUser = () => {
  localStorage.removeItem('accountToken');
  localStorage.removeItem('currentUser');
};
const logout = () => {
  clearUser();
  const redirect = encodeURIComponent(location.pathname + location.search);
  location.href = `/user/login?redirect=${redirect}`;
};
/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, code, msg, showType } =
        res as unknown as ResponseStructure;
      if (code != ApiStatus.success) {
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = { code, msg, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      console.log(error, opts, 'or');
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              const notificationInstance = getNotificationInstance();
              if (notificationInstance) {
                notificationInstance.open({
                  description: errorMessage,
                  message: errorCode,
                });
              } else {
                // 如果实例不可用，使用静态方法作为后备
                notification.open({
                  description: errorMessage,
                  message: errorCode,
                });
              }
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else if (error) {
        console.log(error, 'error');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 1. 添加 token
      const accountToken = localStorage.getItem('accountToken');
      if (accountToken) {
        config.headers = {
          ...config.headers,
          'account-token': accountToken,
        };
      }

      // 拦截请求配置，进行个性化处理。
      const url = config?.url;
      return { ...config, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response: any) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      const { code, msg } = data;
      if (String(code) === String(ApiStatus.success)) return response;
      if (String(code) === String(ApiStatus.unauthorized)) {
        logout();
        return;
      }

      if (
        response.config.responseType &&
        response.config.responseType == 'blob'
      ) {
        return response;
      }
      if (code && msg) {
        message.error(msg);
        return;
      }
      return response;
    },
  ],
};
